import Reveal from "@/components/Reveal";
import MenuCupcakes from "@/components/MenuCupcakes";
import PriceTable from "@/components/PriceTable";
import PriceList from "@/components/PriceList";
import { CakeSlice, Cookie, Sparkles, ArrowRight } from "lucide-react";
import { FROSTING_OPTIONS, CAKE_FLAVORS, CAKE_PRICING, COOKIE_PRICING, SWEET_TREAT_PRICING, CHEESECAKE_PRICING, CHEESECAKE_FLAVORS, IMAGES } from "@/data/site";

function MenuHeading({ number, title, script, testId }) {
    return (
        <Reveal>
            <div className="flex items-baseline gap-5" data-testid={testId}>
                <span className="font-display text-4xl sm:text-5xl font-bold text-[#F8D7E3] select-none leading-none">{number}</span>
                <div>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#3D2318]">{title}</h3>
                    <p className="font-script text-2xl text-[#C95B7D] leading-none mt-1">{script}</p>
                </div>
            </div>
        </Reveal>
    );
}

function Card({ children, className = "", testId }) {
    return (
        <div data-testid={testId} className={`rounded-[2rem] bg-white border border-[#F2E1E6] shadow-[0_16px_40px_rgba(61,35,24,0.08)] p-7 sm:p-9 ${className}`}>
            {children}
        </div>
    );
}

export default function Menu() {
    return (
        <section id="menu" data-testid="menu-section" className="py-16 md:py-24 lg:py-32 bg-[#FDF0F4]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Menu &amp; Pricing</p>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#3D2318]">
                        Everything on the Menu
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-[#6B4A3E] font-light leading-relaxed">
                        Cupcakes by the dozen, layered cakes by the inch, cookies, cheesecakes, and sweet treats — all homemade, all priced up front.
                    </p>
                </Reveal>

                <div className="mt-16 space-y-20">
                    <div>
                        <MenuHeading number="01" title="Cupcakes" script="two ways" testId="menu-heading-cupcakes" />
                        <div className="mt-8"><MenuCupcakes /></div>
                    </div>

                    <div>
                        <MenuHeading number="02" title="Cakes" script="by layers & inches" testId="menu-heading-cakes" />
                        <div className="mt-8 grid lg:grid-cols-12 gap-8">
                            <Reveal className="lg:col-span-7">
                                <div className="rounded-[2rem] bg-[#3D2318] p-7 sm:p-9 shadow-[0_30px_70px_rgba(61,35,24,0.35)] h-full" data-testid="menu-card-cakes">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">Cake pricing</p>
                                    <div className="mt-5"><PriceTable table={CAKE_PRICING} testId="menu-cake-table" dark /></div>
                                    <p className="mt-5 text-xs text-[#FFFDF9]/60 leading-relaxed" data-testid="menu-cake-notes">
                                        Frosted with bettercream unless otherwise requested · Fondant adds $20–40 per order
                                    </p>
                                </div>
                            </Reveal>
                            <Reveal delay={0.1} className="lg:col-span-5">
                                <Card className="h-full flex flex-col" testId="menu-custom-cake-banner">
                                    <div className="flex items-start gap-4">
                                        <CakeSlice className="h-8 w-8 shrink-0 text-[#C59B27]" />
                                        <div>
                                            <h4 className="font-display text-xl sm:text-2xl font-bold text-[#3D2318]">Make it Custom</h4>
                                            <p className="mt-1 text-sm sm:text-base text-[#6B4A3E] leading-relaxed">
                                                Birthday, celebration, holiday, or special event — tell us your theme and colors and we'll create something memorable.
                                            </p>
                                        </div>
                                    </div>
                                    <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">Cake Flavors</p>
                                    <div className="mt-2 flex flex-wrap gap-2" data-testid="menu-cake-flavors">
                                        {[...CAKE_FLAVORS, "Flavor requests welcome!"].map((f) => (
                                            <span key={f} className="rounded-full bg-[#FAF0D7] border border-[#D4AF37]/30 px-3 py-1 text-xs font-medium text-[#6B4A3E]">{f}</span>
                                        ))}
                                    </div>
                                    <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">Frosting Options</p>
                                    <div className="mt-2 flex flex-wrap gap-2" data-testid="menu-frosting-options">
                                        {FROSTING_OPTIONS.map((f) => (
                                            <span key={f} className="rounded-full bg-[#FDF0F4] border border-[#F2E1E6] px-3 py-1 text-xs font-medium text-[#C95B7D]">{f}</span>
                                        ))}
                                    </div>
                                    <a
                                        href="#order"
                                        data-testid="menu-custom-cake-button"
                                        className="group mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#C95B7D] hover:text-[#3D2318] transition-colors duration-300"
                                    >
                                        Ask About a Custom Cake
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </a>
                                </Card>
                            </Reveal>
                        </div>
                    </div>

                    <div>
                        <MenuHeading number="03" title="Cookies & Sweet Treats" script="by the dozen" testId="menu-heading-cookies" />
                        <div className="mt-8 grid md:grid-cols-2 gap-8">
                            <Reveal>
                                <Card className="h-full" testId="menu-card-cookies">
                                    <div className="flex items-center gap-4">
                                        <img src={IMAGES.realCookies} alt="Homemade cookies made by Annabell" className="h-16 w-16 rounded-2xl object-cover" />
                                        <div>
                                            <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]"><Cookie className="h-4 w-4" /> Cookies</p>
                                            <p className="text-sm text-[#6B4A3E]">Baked fresh, by the dozen</p>
                                        </div>
                                    </div>
                                    <div className="mt-5"><PriceList items={COOKIE_PRICING} testId="menu-cookie-list" /></div>
                                </Card>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <Card className="h-full" testId="menu-card-sweet-treats">
                                    <div className="flex items-center gap-4">
                                        <img src={IMAGES.realStrawberries} alt="Chocolate-covered strawberries made by Annabell" className="h-16 w-16 rounded-2xl object-cover" />
                                        <div>
                                            <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]"><Sparkles className="h-4 w-4" /> Sweet Treats</p>
                                            <p className="text-sm text-[#6B4A3E]">Everything $25 per dozen</p>
                                        </div>
                                    </div>
                                    <div className="mt-5"><PriceList items={SWEET_TREAT_PRICING} testId="menu-sweet-treats" /></div>
                                </Card>
                            </Reveal>
                        </div>
                    </div>

                    <div>
                        <MenuHeading number="04" title="Cheesecakes" script="plain or flavored" testId="menu-heading-cheesecakes" />
                        <div className="mt-8 grid lg:grid-cols-12 gap-8">
                            <Reveal className="lg:col-span-7">
                                <Card className="h-full" testId="menu-card-cheesecakes">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">Cheesecake pricing</p>
                                    <div className="mt-5"><PriceTable table={CHEESECAKE_PRICING} testId="menu-cheesecake-table" /></div>
                                </Card>
                            </Reveal>
                            <Reveal delay={0.1} className="lg:col-span-5">
                                <div className="relative h-full min-h-[260px] overflow-hidden rounded-[2rem] shadow-[0_16px_40px_rgba(61,35,24,0.12)]">
                                    <img src={IMAGES.realMiniCheesecakes} alt="Mini cheesecakes made by Annabell" className="absolute inset-0 h-full w-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C180F]/85 via-[#2C180F]/20 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-7">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">Flavored options</p>
                                        <div className="mt-2 flex flex-wrap gap-2" data-testid="menu-cheesecake-flavors">
                                            {[...CHEESECAKE_FLAVORS, "…and more!"].map((f) => (
                                                <span key={f} className="rounded-full bg-[#FFFDF9]/15 backdrop-blur border border-[#FFFDF9]/25 px-3 py-1 text-xs font-medium text-[#FFFDF9]">{f}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
